import { useState, useEffect } from "react";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { cn } from "@/lib/cn";

export function InstallBanner({ className }: { className?: string }) {
  const { isInstallable, promptInstall, dismissPrompt } = useInstallPrompt();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isInstallable) {
      // Delay showing banner by 30s to avoid immediate interruption
      const timer = setTimeout(() => setVisible(true), 30000);
      return () => clearTimeout(timer);
    }
  }, [isInstallable]);

  if (!visible) return null;

  return (
    <div
      className={cn(
        "fixed bottom-20 left-4 right-4 z-50 mx-auto max-w-md rounded-[var(--radius-xl)] border border-[var(--border)] bg-[var(--card)] p-4 shadow-lg",
        className,
      )}
      role="alert"
      aria-label="Install Carmel Work"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-lg)] bg-[var(--color-primary-soft)]">
          <Download className="h-5 w-5 text-[var(--color-primary)]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-[var(--foreground)]">
            Install Carmel Work
          </h3>
          <p className="mt-0.5 text-xs text-[var(--muted-foreground)]">
            Add to your home screen for quick access and offline support.
          </p>
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              className="h-8 text-xs"
              onClick={() => {
                promptInstall();
                setVisible(false);
              }}
            >
              Install
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-xs"
              onClick={() => {
                dismissPrompt();
                setVisible(false);
              }}
            >
              Not now
            </Button>
          </div>
        </div>
        <button
          onClick={() => setVisible(false)}
          className="shrink-0 rounded-full p-1 text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
          aria-label="Dismiss install banner"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
